/**
 * Esquemas de formulario del lienzo de Nova. Cada acción de escritura tiene aquí un formulario
 * "de verdad" —con desplegables, chips y campos tipados— para que rellenarlo sea rápido e
 * intuitivo, en vez de escribir texto crudo (y jamás UUID). Nova pre-rellena lo que ya sabe;
 * la persona completa el resto en el lienzo. Si una acción no tiene esquema, el lienzo cae a un
 * formulario genérico clave/valor (sigue funcionando, sin diseño a medida).
 */

export type CampoTipo =
  | "text"
  | "email"
  | "tel"
  | "number"
  | "currency"
  | "date"
  | "password"
  | "select"
  | "multiselect"
  | "select-remoto"
  | "multiselect-remoto"
  | "idresuelto"
  // Buscador escribe-y-elige de un tercero (proveedor/cliente); guarda su UUID como texto.
  | "tercero-buscar"
  // Líneas repetibles de producto (cada una busca su producto y presentación); vive en estado aparte.
  | "lineas-orden";

/** Fuentes de opciones que se cargan en vivo desde el ERP (para 'select-remoto'/'multiselect-remoto'). */
export type FuenteOpciones = "roles-usuario" | "sedes" | "unidades" | "almacenes";

export interface OpcionCampo {
  value: string;
  label: string;
}

export interface CampoForm {
  key: string;
  label: string;
  tipo: CampoTipo;
  requerido?: boolean;
  placeholder?: string;
  ayuda?: string;
  opciones?: OpcionCampo[];
  porDefecto?: string | string[];
  /** Para 'idresuelto': qué tipo de id resolver a nombre (vía useNombres). */
  resuelve?: "producto" | "presentacion" | "tercero";
  /** Para 'select-remoto'/'multiselect-remoto': de dónde salen las opciones (en vivo). */
  fuente?: FuenteOpciones;
  /** Para 'tercero-buscar': filtra el buscador por rol ('supplier', 'customer'…); sin él, todos. */
  rolTercero?: string;
}

/** True si el campo guarda una lista (multiselección, local o remota). */
export function esMultiple(tipo: CampoTipo): boolean {
  return tipo === "multiselect" || tipo === "multiselect-remoto";
}

export interface FormAccion {
  titulo: string;
  intro?: string;
  cta: string;
  campos: CampoForm[];
}

const TIPO_TERCERO: OpcionCampo[] = [
  { value: "organization", label: "Empresa" },
  { value: "person", label: "Persona" },
];

const TIPO_IDENT: OpcionCampo[] = [
  { value: "NIT", label: "NIT" },
  { value: "CC", label: "Cédula de ciudadanía (CC)" },
  { value: "CE", label: "Cédula de extranjería (CE)" },
  { value: "PASSPORT", label: "Pasaporte" },
  { value: "NUIP", label: "NUIP" },
  { value: "OTHER", label: "Otro" },
];

const ROLES: OpcionCampo[] = [
  { value: "supplier", label: "Proveedor" },
  { value: "customer", label: "Cliente" },
  { value: "employee", label: "Empleado" },
  { value: "carrier", label: "Transportador" },
  { value: "insurer", label: "Aseguradora" },
  { value: "other", label: "Otro" },
];

const PAISES: OpcionCampo[] = [
  { value: "CO", label: "Colombia (CO)" },
  { value: "US", label: "Estados Unidos (US)" },
  { value: "EC", label: "Ecuador (EC)" },
  { value: "PA", label: "Panamá (PA)" },
];

const SI_NO: OpcionCampo[] = [
  { value: "true", label: "Sí" },
  { value: "false", label: "No" },
];

const TIPO_PRODUCTO: OpcionCampo[] = [
  { value: "medication", label: "Medicamento" },
  { value: "device", label: "Dispositivo" },
  { value: "cosmetic", label: "Cosmético" },
  { value: "supplement", label: "Suplemento" },
  { value: "food", label: "Alimento" },
  { value: "general", label: "General" },
];

export const FORMULARIOS: Record<string, FormAccion> = {
  crear_producto: {
    titulo: "Nuevo producto",
    intro: "Registra un producto del catálogo. Nace en borrador; su precio y presentación van aparte.",
    cta: "Crear producto",
    campos: [
      { key: "sku", label: "SKU / código", tipo: "text", requerido: true, placeholder: "p. ej. DOLEX500" },
      { key: "name", label: "Nombre", tipo: "text", requerido: true, placeholder: "p. ej. Dolex 500 mg" },
      { key: "itemType", label: "Tipo", tipo: "select", requerido: true, opciones: TIPO_PRODUCTO, porDefecto: "medication" },
      {
        key: "baseUnitId",
        label: "Unidad base",
        tipo: "select-remoto",
        requerido: true,
        fuente: "unidades",
        ayuda: "La unidad de medida base del producto.",
      },
      { key: "requiresLot", label: "¿Exige lote?", tipo: "select", requerido: true, opciones: SI_NO, porDefecto: "false" },
      {
        key: "requiresExpiry",
        label: "¿Exige vencimiento?",
        tipo: "select",
        requerido: true,
        opciones: SI_NO,
        porDefecto: "false",
        ayuda: "Si exige vencimiento, también debe exigir lote.",
      },
      { key: "allowsFraction", label: "¿Permite fracción?", tipo: "select", requerido: true, opciones: SI_NO, porDefecto: "false" },
    ],
  },
  crear_usuario: {
    titulo: "Nuevo usuario",
    intro: "Da de alta a una persona en el ERP y su rol. La contraseña es temporal: la cambiará al entrar.",
    cta: "Crear usuario",
    campos: [
      { key: "username", label: "Usuario", tipo: "text", requerido: true, placeholder: "p. ej. jperez" },
      { key: "firstName", label: "Nombre", tipo: "text", requerido: true },
      { key: "lastName", label: "Apellido", tipo: "text", placeholder: "Opcional" },
      { key: "email", label: "Correo", tipo: "email", placeholder: "Opcional" },
      { key: "documentId", label: "Documento", tipo: "text", placeholder: "Opcional" },
      {
        key: "preset",
        label: "Rol",
        tipo: "select-remoto",
        requerido: true,
        fuente: "roles-usuario",
        ayuda: "El rol define qué puede hacer.",
      },
      {
        key: "temporaryPassword",
        label: "Contraseña temporal",
        tipo: "password",
        requerido: true,
        placeholder: "Mínimo 8 caracteres",
        ayuda: "La cambiará la primera vez que entre.",
      },
      {
        key: "branchIds",
        label: "Sedes",
        tipo: "multiselect-remoto",
        fuente: "sedes",
        ayuda: "En qué sedes trabaja (opcional).",
      },
      { key: "enabled", label: "Activo", tipo: "select", requerido: true, opciones: SI_NO, porDefecto: "true" },
    ],
  },
  crear_tercero: {
    titulo: "Nuevo tercero",
    intro: "Cliente, proveedor o ambos. Rellena lo esencial; Nova completó lo que le dijiste.",
    cta: "Crear tercero",
    campos: [
      {
        key: "legalName",
        label: "Razón social / Nombre",
        tipo: "text",
        requerido: true,
        placeholder: "p. ej. Coopidrogas SAS",
      },
      { key: "tradeName", label: "Nombre comercial", tipo: "text", placeholder: "Opcional" },
      {
        key: "partyType",
        label: "Tipo",
        tipo: "select",
        requerido: true,
        opciones: TIPO_TERCERO,
        porDefecto: "organization",
      },
      {
        key: "identificationType",
        label: "Tipo de identificación",
        tipo: "select",
        requerido: true,
        opciones: TIPO_IDENT,
        porDefecto: "NIT",
      },
      {
        key: "identificationNumber",
        label: "Número de identificación",
        tipo: "text",
        requerido: true,
        placeholder: "Solo dígitos",
      },
      {
        key: "countryCode",
        label: "País",
        tipo: "select",
        requerido: true,
        opciones: PAISES,
        porDefecto: "CO",
      },
      {
        key: "roles",
        label: "Roles",
        tipo: "multiselect",
        requerido: true,
        opciones: ROLES,
        ayuda: "Puede ser varios a la vez.",
      },
      { key: "email", label: "Correo", tipo: "email", placeholder: "Opcional" },
      { key: "phone", label: "Teléfono", tipo: "tel", placeholder: "Opcional" },
    ],
  },
  crear_orden_compra: {
    titulo: "Nueva orden de compra",
    intro: "Pide productos a un proveedor. Busca cada producto por su nombre; el resto ya lo sabe Nova.",
    cta: "Crear orden",
    campos: [
      {
        key: "supplierThirdPartyId",
        label: "Proveedor",
        tipo: "tercero-buscar",
        requerido: true,
        rolTercero: "supplier",
        ayuda: "Búscalo por NIT o nombre.",
      },
      {
        key: "destinationWarehouseId",
        label: "Almacén de destino",
        tipo: "select-remoto",
        requerido: true,
        fuente: "almacenes",
        ayuda: "Dónde entrará la mercancía.",
      },
      {
        key: "expectedOn",
        label: "Fecha esperada de entrega",
        tipo: "date",
        requerido: true,
      },
      {
        key: "justification",
        label: "Motivo",
        tipo: "text",
        requerido: true,
        placeholder: "p. ej. Reposición mensual de analgésicos",
        ayuda: "Mínimo 3 caracteres.",
      },
      {
        key: "lines",
        label: "Productos",
        tipo: "lineas-orden",
        requerido: true,
        ayuda: "Agrega una línea por producto: búscalo, elige su presentación e indica la cantidad.",
      },
    ],
  },
  cancelar_orden_compra: {
    titulo: "Cancelar orden de compra",
    intro: "Se cancelará la orden seleccionada. Indica el motivo (queda en la auditoría).",
    cta: "Cancelar orden",
    campos: [
      {
        key: "reason",
        label: "Motivo",
        tipo: "text",
        requerido: true,
        placeholder: "p. ej. Proveedor sin stock",
        ayuda: "Mínimo 3 caracteres.",
      },
    ],
  },
  emitir_orden_compra: {
    titulo: "Emitir orden de compra",
    intro: "La orden pasará a firme (emitida). Añade un motivo o nota.",
    cta: "Emitir orden",
    campos: [
      {
        key: "reason",
        label: "Motivo o nota",
        tipo: "text",
        requerido: true,
        placeholder: "p. ej. Aprobada por compras",
        ayuda: "Mínimo 3 caracteres.",
      },
    ],
  },
  actualizar_precio: {
    titulo: "Actualizar precio de venta",
    intro: "Nova ya eligió el producto y la lista. Fija el nuevo precio (gana la última versión).",
    cta: "Actualizar precio",
    campos: [
      { key: "productId", label: "Producto", tipo: "idresuelto", resuelve: "producto" },
      { key: "presentationId", label: "Presentación", tipo: "idresuelto", resuelve: "presentacion" },
      { key: "priceListId", label: "Lista de precios", tipo: "idresuelto" },
      {
        key: "unitPrice",
        label: "Nuevo precio unitario",
        tipo: "currency",
        requerido: true,
        placeholder: "0",
      },
      {
        key: "effectiveOn",
        label: "Vigente desde",
        tipo: "date",
        ayuda: "Si lo dejas vacío, aplica hoy.",
      },
    ],
  },
};

export function esquemaDe(tool: string): FormAccion | null {
  return FORMULARIOS[tool] ?? null;
}

/** Normaliza un valor de parámetro a lo que el control espera (arreglo para multiselect). */
function normaliza(valor: unknown): string | string[] {
  if (Array.isArray(valor)) {
    return valor.map((v) => String(v));
  }
  if (valor == null) {
    return "";
  }
  return String(valor);
}

/**
 * Valores iniciales del formulario: mezcla los parámetros que propuso Nova con los valores por
 * defecto del esquema (los de Nova ganan). Para acciones sin esquema, refleja tal cual los params.
 */
export function valoresIniciales(tool: string, params: Record<string, unknown>): Record<string, string | string[]> {
  const esquema = esquemaDe(tool);
  const out: Record<string, string | string[]> = {};
  if (esquema) {
    for (const campo of esquema.campos) {
      const propuesto = params[campo.key];
      if (propuesto !== undefined && propuesto !== null && propuesto !== "") {
        out[campo.key] = esMultiple(campo.tipo) ? normalizaLista(propuesto) : normaliza(propuesto);
      } else if (campo.porDefecto !== undefined) {
        out[campo.key] = Array.isArray(campo.porDefecto) ? [...campo.porDefecto] : campo.porDefecto;
      } else {
        out[campo.key] = esMultiple(campo.tipo) ? [] : "";
      }
    }
    // Conserva cualquier parámetro extra que Nova mandara y no esté en el esquema.
    for (const [k, v] of Object.entries(params)) {
      if (!(k in out)) {
        out[k] = normaliza(v);
      }
    }
    return out;
  }
  for (const [k, v] of Object.entries(params)) {
    out[k] = normaliza(v);
  }
  return out;
}

function normalizaLista(valor: unknown): string[] {
  if (Array.isArray(valor)) {
    return valor.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof valor === "string") {
    return valor
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** Reconstruye los parámetros a enviar desde los valores del formulario (arreglos intactos). */
export function valoresAParams(
  valores: Record<string, string | string[]>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(valores)) {
    if (Array.isArray(v)) {
      out[k] = v;
    } else {
      const t = v.trim();
      if (t !== "") {
        out[k] = t;
      }
    }
  }
  return out;
}

/** Campos requeridos del esquema que siguen vacíos (para bloquear el botón y avisar). */
export function faltantes(tool: string, valores: Record<string, string | string[]>): string[] {
  const esquema = esquemaDe(tool);
  if (!esquema) {
    return [];
  }
  const out: string[] = [];
  for (const campo of esquema.campos) {
    if (!campo.requerido) {
      continue;
    }
    // Las líneas viven en estado aparte (no en 'valores'); se validan en el propio control.
    if (campo.tipo === "lineas-orden") {
      continue;
    }
    const v = valores[campo.key];
    const vacio = Array.isArray(v) ? v.length === 0 : !v || v.trim() === "";
    if (vacio) {
      out.push(campo.label);
    }
  }
  return out;
}

/**
 * Una línea de una orden de compra tal como la arma el lienzo. El backend sólo necesita
 * productId, presentationId, quantity y (opcional) expectedUnitPrice; las etiquetas son para
 * mostrar el nombre real en la fila y el resumen, y el backend las ignora.
 */
export interface LineaOrden {
  /** Id de cliente estable (para keys de React y actualizaciones por línea); no viaja al backend. */
  uid: string;
  productId: string;
  productLabel: string;
  presentationId: string;
  presentationLabel: string;
  quantity: string;
  // Términos comerciales ESPERADOS, opcionales pero todo-o-nada: el ERP exige precio + impuesto +
  // flete completos o los tres vacíos. El flete se asume 0 si se deja en blanco con precio e IVA.
  expectedUnitPrice: string;
  expectedTaxRatePercent: string;
  expectedFreightPerUnit: string;
}

export function lineaVacia(): LineaOrden {
  return {
    uid: crypto.randomUUID(),
    productId: "",
    productLabel: "",
    presentationId: "",
    presentationLabel: "",
    quantity: "",
    expectedUnitPrice: "",
    expectedTaxRatePercent: "",
    expectedFreightPerUnit: "",
  };
}

/**
 * Los términos esperados van completos (precio + IVA; el flete se asume 0) o vacíos. Una línea con
 * ALGÚN término pero sin la base (precio e IVA) está incompleta y el ERP la rechazaría: se bloquea.
 */
export function terminosParciales(l: LineaOrden): boolean {
  const algo =
    l.expectedUnitPrice.trim() !== "" ||
    l.expectedTaxRatePercent.trim() !== "" ||
    l.expectedFreightPerUnit.trim() !== "";
  const base = l.expectedUnitPrice.trim() !== "" && l.expectedTaxRatePercent.trim() !== "";
  return algo && !base;
}

/** True si alguna línea completa tiene términos comerciales a medias (para bloquear el envío). */
export function hayTerminosParciales(lineas: LineaOrden[]): boolean {
  return lineas.filter(lineaCompleta).some(terminosParciales);
}

/** Una línea está completa (y cuenta para el envío) si tiene producto, presentación y cantidad. */
export function lineaCompleta(l: LineaOrden): boolean {
  return l.productId !== "" && l.presentationId !== "" && l.quantity.trim() !== "";
}

/** True si el formulario de esta acción incluye un campo de líneas (orden de compra). */
export function esquemaTieneLineas(tool: string): boolean {
  return esquemaDe(tool)?.campos.some((c) => c.tipo === "lineas-orden") ?? false;
}

/**
 * Convierte las líneas del lienzo al arreglo que viaja en la acción. Incluye las etiquetas
 * (productLabel/presentationLabel) para que el resumen de aprobación muestre nombres reales; el
 * backend sólo lee productId, presentationId, quantity y expectedUnitPrice, e ignora el resto.
 */
export function lineasAParams(lineas: LineaOrden[]): Array<Record<string, string>> {
  return lineas.filter(lineaCompleta).map((l) => {
    const linea: Record<string, string> = {
      productId: l.productId,
      presentationId: l.presentationId,
      quantity: l.quantity.trim(),
      productLabel: l.productLabel,
      presentationLabel: l.presentationLabel,
    };
    const precio = l.expectedUnitPrice.trim();
    const iva = l.expectedTaxRatePercent.trim();
    // El ERP exige los términos completos (precio + impuesto + flete) o ninguno. Sólo se envían
    // cuando hay precio E IVA; el flete cae a 0 si se dejó vacío. Si están a medias, la validación
    // del lienzo ya bloqueó el envío, así que aquí no se mandan términos parciales.
    if (precio !== "" && iva !== "") {
      linea["expectedUnitPrice"] = precio;
      linea["expectedTaxRatePercent"] = iva;
      linea["expectedFreightPerUnit"] = l.expectedFreightPerUnit.trim() || "0";
    }
    return linea;
  });
}
